// Routing
import { Link } from 'react-router-dom'
// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faLink,
  faListCheck,
  faShieldCheck,
  faReply,
  faArrowRight,
  faRocketLaunch,
  faSparkles,
  faBoxesStacked,
  faFileImport,
  faPaperPlane
} from '@fortawesome/pro-light-svg-icons'

const FeatureCard: React.FC<{
  icon: any
  color: string
  step: number
  title: string
  description: string
  details: string[]
}> = ({ icon, color, step, title, description, details }) => (
  <div className='group relative bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden'>
    <div className={`absolute top-0 left-0 w-full h-1 bg-linear-to-r ${color}`} />
    <div className='p-8'>
      <div className='flex items-start gap-5'>
        <div className='shrink-0'>
          <div className='relative'>
            <div
              className={`w-14 h-14 rounded-xl bg-linear-to-br ${color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}
            >
              <FontAwesomeIcon icon={icon} className='text-white text-2xl' />
            </div>
            <div className='absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gray-900 text-white text-xs font-bold flex items-center justify-center'>
              {step}
            </div>
          </div>
        </div>
        <div>
          <h3 className='text-xl font-bold text-gray-900 mb-2'>{title}</h3>
          <p className='text-gray-600 mb-4 leading-relaxed'>{description}</p>
          <ul className='space-y-2'>
            {details.map((detail, i) => (
              <li key={i} className='flex items-start gap-2 text-sm text-gray-500'>
                <FontAwesomeIcon icon={faSparkles} className='text-indigo-400 mt-0.5 shrink-0' />
                <span>{detail}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </div>
)

export default function PageApril2026RFQ() {
  return (
    <div className='min-h-screen bg-linear-to-b from-gray-50 to-white'>
      {/* Hero */}
      <div className='relative overflow-hidden bg-linear-to-br from-indigo-600 via-purple-600 to-indigo-700'>
        <div className='absolute inset-0 opacity-10'>
          <div className='absolute inset-0 bg-[linear-gradient(to_right,#ffffff10_1px,transparent_1px),linear-gradient(to_bottom,#ffffff10_1px,transparent_1px)] bg-size-[40px_40px]' />
        </div>
        <div className='absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl bg-purple-400/20' />
        <div className='absolute -bottom-40 -left-40 w-96 h-96 rounded-full blur-3xl bg-indigo-400/20' />

        <div className='relative max-w-4xl mx-auto px-6 py-20 text-center'>
          <div className='inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-sm text-indigo-100 mb-6'>
            <FontAwesomeIcon icon={faRocketLaunch} />
            <span>New Feature &mdash; April 2026</span>
          </div>
          <h1 className='text-4xl md:text-5xl font-extrabold text-white mb-6 tracking-tight leading-tight'>
            Introducing Request for Quote
          </h1>
          <p className='text-lg md:text-xl text-indigo-100 max-w-2xl mx-auto leading-relaxed mb-10'>
            A smarter way to source products. Import URLs, build detailed multi-line RFQs, route them through approvals,
            and receive competitive quotes linked directly to your Marketplace and catalogs.
          </p>
          <Link
            to='/rfq/dashboard'
            className='inline-flex items-center gap-2 bg-white text-indigo-700 font-semibold px-8 py-3 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300'
          >
            Try It Now
            <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className='max-w-4xl mx-auto px-6 py-16'>
        {/* Intro */}
        <div className='text-center mb-16'>
          <h2 className='text-3xl font-bold text-gray-900 mb-4'>How It Works</h2>
          <p className='text-gray-600 max-w-2xl mx-auto text-lg leading-relaxed'>
            The Request for Quote system streamlines your procurement workflow from start to finish. Here&rsquo;s
            everything you can do.
          </p>
        </div>

        {/* Feature Cards */}
        <div className='space-y-8 mb-16'>
          <FeatureCard
            icon={faFileImport}
            color='from-blue-500 to-blue-600'
            step={1}
            title='Import Product URLs'
            description="Found what you need on a supplier's website? Paste the URL directly into your RFQ. MainStem will pull in the product details so you don't have to enter them manually."
            details={[
              'Paste any product URL and MainStem extracts the details automatically',
              'Supports URLs from any supplier website',
              'Saves time by eliminating manual product data entry'
            ]}
          />

          <FeatureCard
            icon={faListCheck}
            color='from-purple-500 to-purple-600'
            step={2}
            title='Build Multi-Line RFQs'
            description='Need quotes on multiple items at once? Build comprehensive, multi-line RFQs with quantities, specifications, and notes for each line item — all in a single request.'
            details={[
              'Add as many line items as you need in a single RFQ',
              'Specify quantities, preferred brands, and detailed requirements per line',
              'Attach notes and special instructions for suppliers'
            ]}
          />

          <FeatureCard
            icon={faShieldCheck}
            color='from-green-500 to-green-600'
            step={3}
            title='Get Approval Before Sending'
            description="RFQs flow through your organization's existing approval workflows before they go out. Approvers can review line items, make adjustments, and approve or reject — keeping procurement aligned with your policies."
            details={[
              'RFQs are routed through your configured approval chains',
              'Approvers can review, comment on, and adjust line items',
              'Full audit trail of who approved and when'
            ]}
          />

          <FeatureCard
            icon={faReply}
            color='from-orange-500 to-orange-600'
            step={4}
            title='Receive Quotes Linked to Your Catalogs'
            description="Once approved, MainStem's team responds with competitive quotes tied directly to products in the Marketplace or your organization's catalogs. Compare pricing, review options, and convert to purchase orders with a single click."
            details={[
              'Quotes are linked to real products in the MainStem Marketplace',
              'Products matched to your existing catalogs where available',
              'One-click conversion from accepted quote to purchase order'
            ]}
          />
        </div>

        {/* Summary Banner */}
        <div className='relative overflow-hidden rounded-2xl bg-linear-to-r from-indigo-50 via-purple-50 to-indigo-50 border border-indigo-100 p-10 mb-16'>
          <div className='absolute -top-20 -right-20 w-56 h-56 rounded-full blur-3xl bg-indigo-400/10' />
          <div className='relative flex flex-col md:flex-row items-center gap-8'>
            <div className='shrink-0'>
              <div className='w-16 h-16 rounded-2xl bg-linear-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg'>
                <FontAwesomeIcon icon={faBoxesStacked} className='text-white text-3xl' />
              </div>
            </div>
            <div className='text-center md:text-left'>
              <h3 className='text-2xl font-bold text-gray-900 mb-2'>Streamline Your Sourcing</h3>
              <p className='text-gray-600 leading-relaxed'>
                Whether you need a single hard-to-find item or a bulk order across dozens of SKUs, the RFQ system keeps
                everything organized, approved, and connected to your purchasing workflow. No more spreadsheets, no more
                email chains — just faster, smarter procurement.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className='text-center'>
          <h3 className='text-2xl font-bold text-gray-900 mb-3'>Ready to Get Started?</h3>
          <p className='text-gray-600 mb-8 max-w-lg mx-auto'>
            Head to the RFQ Dashboard to create your first Request for Quote and experience the new workflow.
          </p>
          <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
            <Link
              to='/rfq/dashboard'
              className='inline-flex items-center gap-2 bg-linear-to-r from-indigo-600 to-purple-600 text-white font-semibold px-8 py-3 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300'
            >
              <FontAwesomeIcon icon={faPaperPlane} />
              Go to RFQ Dashboard
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
